import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useOpsAgencyId } from '@/services/opsRota/agency';
import { opsRotaClient, OpsRotaApiError } from '@/services/opsRota/client';
import { opsRotaQueryKeys } from '@/services/opsRota/queryKeys';

// DB-06 / BR-07: an edit must reach the dashboard within one minute, so poll
// every 30 s (a 60 s poll plus request time can miss the minute). Same
// cadence as Dashboard V2's liveQuery.ts. These options override the app-wide
// QueryClient (staleTime Infinity, no focus refetch) for this query only.
export const OPS_ON_DUTY_REFRESH_MS = 30_000;

// A 5xx that survives its one retry (say graphy is up but the ops
// tables are not) backs polling off
// to 5 minutes, so every open dashboard does not hit the error twice a
// minute. The next success brings the 30 s cadence back.
export const OPS_ON_DUTY_BACKOFF_MS = 5 * 60_000;

/** The browser's IANA zone: "today" is the viewer's local date (DB-04). */
export const viewerTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';

// A 404 means graphy has not shipped the endpoint yet: stop asking.
const isMissing = (error: unknown) =>
  error instanceof OpsRotaApiError && error.status === 404;

const isServerError = (error: unknown) =>
  error instanceof OpsRotaApiError &&
  error.status !== null &&
  error.status >= 500;

// One retry, this long after a 5xx, before the bar hides: a single blip
// would otherwise hide it for the whole back-off.
export const OPS_ON_DUTY_RETRY_DELAY_MS = 2_000;

// Focus and tab switches wait this long after a 5xx, so switching back
// and forth does not hammer a failing graphy. It is the normal poll cadence,
// not the 5-minute back-off: coming back to the tab is the moment someone
// looks at the bar, so it asks again rather than leaving it hidden.
const inFocusBackoff = (error: unknown, errorUpdatedAt: number) =>
  isServerError(error) && Date.now() - errorUpdatedAt < OPS_ON_DUTY_REFRESH_MS;

/** The poll interval after the last fetch: off, backed off, or 30 s. */
export const nextRefetchMs = (
  status: 'pending' | 'error' | 'success',
  error: unknown,
): number | false => {
  if (status !== 'error') return OPS_ON_DUTY_REFRESH_MS;
  if (isMissing(error)) return false;
  return isServerError(error) ? OPS_ON_DUTY_BACKOFF_MS : OPS_ON_DUTY_REFRESH_MS;
};

export const useOpsOnDuty = () => {
  const timeZone = viewerTimeZone();
  const agencyId = useOpsAgencyId();
  const query = useQuery({
    queryKey: opsRotaQueryKeys.onDuty(timeZone, agencyId),
    queryFn: () => opsRotaClient.getOnDuty({ timeZone }),
    staleTime: OPS_ON_DUTY_REFRESH_MS,
    // `status` is the last fetch's outcome: an error that follows earlier
    // data still reads 'error', and a success clears it.
    refetchInterval: (q) => nextRefetchMs(q.state.status, q.state.error),
    refetchIntervalInBackground: false,
    // Tab switches (react-query 5 listens to visibilitychange only).
    refetchOnWindowFocus: (q) =>
      !isMissing(q.state.error) &&
      !inFocusBackoff(q.state.error, q.state.errorUpdatedAt) &&
      'always',
    refetchOnReconnect: (q) => !isMissing(q.state.error),
    retry: (failureCount, error) => !isMissing(error) && failureCount < 1,
    retryDelay: OPS_ON_DUTY_RETRY_DELAY_MS,
  });

  // DB-06 "on page focus": coming back to the browser window from another
  // app fires `focus` but not `visibilitychange`, which react-query ignores.
  const { refetch, error, errorUpdatedAt } = query;
  useEffect(() => {
    if (isMissing(error)) return undefined;
    const onFocus = () => {
      if (inFocusBackoff(error, errorUpdatedAt)) return;
      // cancelRefetch: false joins a fetch already in flight (a tab switch
      // fires both events) instead of cancelling it and asking again.
      refetch({ cancelRefetch: false });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refetch, error, errorUpdatedAt]);

  return query;
};
